import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import JSZip from 'jszip';

import BaseStyle from '../assets/css/Dashboard.module.css';
import styles from '../assets/css/Anpr.module.css';
import { useTheme } from '../provider/themeProvider';
import { useDesktop } from '../provider/DesktopContext';
import { useAppContext } from '../provider/appProvider';
import { useAuth } from '../provider/authProvider';
import api from '../services/api';

const SORT_OPTIONS = [
  { value: 'detected_at', label: 'Date' },
  { value: 'confidence_level', label: 'Confiance' },
  { value: 'license_plate', label: 'Plaque' },
];

function confidenceClass(level) {
  if (level == null) return '';
  if (level >= 80) return styles.confidenceHigh;
  if (level >= 60) return styles.confidenceMed;
  return styles.confidenceLow;
}

const DIRECTION_LABELS = {
  forward:     "S'approche",
  reverse:     "S'éloigne",
  approaching: 'Approche',
  leaving:     'Départ',
  both:        'Les deux',
};

const COLOR_LABELS = {
  white:       'Blanc',
  black:       'Noir',
  gray:        'Gris',
  grey:        'Gris',
  silver:      'Argent',
  red:         'Rouge',
  blue:        'Bleu',
  yellow:      'Jaune',
  green:       'Vert',
  orange:      'Orange',
  brown:       'Marron',
  purple:      'Violet',
  pink:        'Rose',
  gold:        'Or',
  beige:       'Beige',
  cyan:        'Cyan',
  unknown:     'Inconnu',
};

function translateDirection(value) {
  if (!value) return null;
  return DIRECTION_LABELS[value.toLowerCase()] ?? value;
}

function translateColor(value) {
  if (!value) return null;
  return COLOR_LABELS[value.toLowerCase()] ?? value;
}

function PlateChars({ plate, uncertainChars = [] }) {
  const uncertainMap = new Map(uncertainChars.map(c => [c.position, c]));
  if (plate === "unknown") {
    return (
      <span>
          Plaque non reconnue
        </span>
    )
  } else {
    return (
      <span className={styles.plateText}>
      {plate.split('').map((char, i) => {
        const uncertain = uncertainMap.get(i);
        return (
          <span
            key={i}
            className={uncertain ? styles.plateCharUncertain : styles.plateCharSafe}
            title={uncertain ? `Confiance: ${uncertain.confidence}%` : undefined}
          >
            {char}
          </span>
        );
      })}
    </span>
    );
  }
}

async function fetchBlob(url) {
  const res = await fetch(url);
  if (!res.ok) return null;
  return res.blob();
}

async function downloadImage(url, filename) {
  const blob = await fetchBlob(url);
  if (!blob) return;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function systemLabel(system) {
  return system?.name_displayed ?? system?.name ?? system?.title ?? '—';
}

function buildInfoText(detection) {
  const lines = [
    `Plaque       : ${detection.license_plate}`,
    ...(detection.system ? [`Site         : ${systemLabel(detection.system)}`] : []),
    `Date         : ${dayjs(detection.detected_at).format('DD/MM/YYYY à HH:mm:ss')}`,
    `Caméra       : ${detection.camera?.name_displayed ?? detection.camera?.name ?? '—'}`,
  ];
  if (detection.confidence_level != null)
    lines.push(`Confiance    : ${detection.confidence_level}%`);
  if (detection.char_confidence_avg != null)
    lines.push(`Conf. chars  : ${detection.char_confidence_avg}%`);
  if (detection.vehicle_color)
    lines.push(`Couleur      : ${translateColor(detection.vehicle_color)}`);
  if (detection.direction)
    lines.push(`Direction    : ${translateDirection(detection.direction)}`);
  if (detection.known_plate) {
    const label = detection.known_plate.user
      ? `${detection.known_plate.user.first_name} ${detection.known_plate.user.last_name}`
      : (detection.known_plate.name_displayed ?? detection.known_plate.name);
    lines.push(`Plaque connue: ${label}`);
  }
  return lines.join('\n');
}

async function downloadSelectionAsZip(detections, onProgress) {
  const zip = new JSZip();

  for (let i = 0; i < detections.length; i++) {
    const d = detections[i];
    onProgress(i, detections.length);

    const plate = d.license_plate ?? 'inconnu';
    const dateSlug = dayjs(d.detected_at).format('YYYYMMDD_HHmmss');
    const folder = zip.folder(`${plate}_${dateSlug}`);

    const images = d.images ?? {};
    const fetches = [
      images.plate      ? fetchBlob(images.plate).then(b => b && folder.file('plaque.jpg', b))      : null,
      images.vehicle    ? fetchBlob(images.vehicle).then(b => b && folder.file('vehicule.jpg', b))   : null,
      images.detection  ? fetchBlob(images.detection).then(b => b && folder.file('contexte.jpg', b)) : null,
    ].filter(Boolean);

    await Promise.all(fetches);
    folder.file('info.txt', buildInfoText(d));
  }

  onProgress(detections.length, detections.length);
  const blob = await zip.generateAsync({ type: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `anpr_export_${dayjs().format('YYYYMMDD_HHmmss')}.zip`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function DetectionCard({ detection, expanded, onClick, selected, onSelect, showSystem, isOtherSystem, onSwitchSystem }) {
  const images = detection.images ?? {};
  const hasImages = images.plate || images.vehicle || images.detection;
  const plate = detection.license_plate ?? 'unknown';
  const dateSlug = dayjs(detection.detected_at).format('YYYYMMDD_HHmmss');

  const [recordStatus, setRecordStatus] = useState(null); // null | 'pending' | 'success' | 'error'
  const [recordId, setRecordId] = useState(null);
  const pollRef = useRef(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxSrc(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxSrc]);

  const startPolling = (id) => {
    const deadline = Date.now() + 2 * 60 * 1000;
    pollRef.current = setInterval(async () => {
      if (Date.now() > deadline) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRecordStatus('error');
        return;
      }
      try {
        const poll = await api.get(`/records/${id}`);
        if (poll.data?.record?.pending === false) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRecordStatus('success');
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRecordStatus('error');
      }
    }, 3000);
  };

  useEffect(() => {
    if (detection.record) {
      setRecordId(detection.record.id);
      if (!detection.record.pending) {
        setRecordStatus('success');
      } else {
        setRecordStatus('pending');
        startPolling(detection.record.id);
      }
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleRequestRecord = async (e) => {
    e.stopPropagation();
    setRecordStatus('pending');
    try {
      const res = await api.post(`/cameras/${detection.camera.id}/record`, {
        targetTime: detection.detected_at,
        secondsBefore: 15,
        secondsAfter: 15,
      });
      const id = res.data?.recordId;
      if (!id) { setRecordStatus('error'); return; }
      setRecordId(id);
      await api.patch(`/anpr-detections/${detection.id}/record`, { records_id: id });
      startPolling(id);
    } catch {
      setRecordStatus('error');
    }
  };

  let cardClass = styles.detectionCard;
  if (expanded) cardClass = styles.detectionCardExpanded;
  if (selected) cardClass = styles.detectionCardSelected;

  return (
    <div className={cardClass} onClick={onClick}>
      <div className={styles.cardMain}>
        <div
          className={styles.cardCheckbox}
          onClick={e => { e.stopPropagation(); onSelect(detection.id); }}
        >
          <input type="checkbox" readOnly checked={selected} className={styles.checkbox} />
        </div>
        {images.plate || images.vehicle || images.detection ? (
          <img
            className={styles.plateImage}
            src={images.plate ?? images.vehicle ?? images.detection}
            alt="Plaque"
            style={{ cursor: 'zoom-in' }}
            onClick={e => { e.stopPropagation(); setLightboxSrc(images.plate ?? images.vehicle ?? images.detection); }}
          />
        ) : (
          <div className={styles.plateImagePlaceholder}></div>
        )}

        <div className={styles.cardInfo}>
          <div className={styles.plateRow}>
            <PlateChars
              plate={detection.license_plate}
              uncertainChars={detection.uncertain_chars}
            />
            {detection.char_confidence_avg != null && (
              <span className={confidenceClass(detection.char_confidence_avg)}>
                {detection.char_confidence_avg}%
              </span>
            )}
            {detection.known_plate && (
              <span className={styles.knownBadge}>
                {detection.known_plate.user
                  ? `${detection.known_plate.user.first_name} ${detection.known_plate.user.last_name}`
                  : (detection.known_plate.name_displayed ?? detection.known_plate.name)}
              </span>
            )}
          </div>

          <div className={styles.metaRow}>
            {detection.vehicle_color && <span>{translateColor(detection.vehicle_color)}</span>}
            {detection.direction && <span>{translateDirection(detection.direction)}</span>}
          </div>
        </div>

        <div className={styles.cardRight}>
          {showSystem && detection.system && (
            <span className={styles.systemBadge}>{systemLabel(detection.system)}</span>
          )}
          <span className={styles.cameraName}>
            {detection.camera?.name_displayed ?? detection.camera?.name ?? '—'}
          </span>
          <span className={styles.dateText}>
            {dayjs(detection.detected_at).format('DD/MM/YYYY HH:mm')}
          </span>
        </div>
      </div>

      {lightboxSrc && createPortal(
        <div className={styles.lightboxOverlay} onClick={e => { e.stopPropagation(); setLightboxSrc(null); }}>
          <div className={styles.lightboxActions} onClick={e => e.stopPropagation()}>
            <button
              className={styles.lightboxBtn}
              title="Télécharger"
              onClick={e => { e.stopPropagation(); downloadImage(lightboxSrc, lightboxSrc.split('/').pop().split('?')[0] || 'image.jpg'); }}
            >
              ↓
            </button>
            <button className={styles.lightboxBtn} title="Fermer" onClick={e => { e.stopPropagation(); setLightboxSrc(null); }}>✕</button>
          </div>
          <img
            className={styles.lightboxImg}
            src={lightboxSrc}
            alt="Aperçu"
            onClick={e => e.stopPropagation()}
          />
        </div>,
        document.body
      )}

      {expanded && (
        <div className={styles.detailPanel} onClick={e => e.stopPropagation()}>
          {hasImages && (
            <>
              {images.plate && (
                <div className={styles.detailImage}>
                  <img className={styles.detailImg} src={images.plate} alt="Plaque" style={{ cursor: 'zoom-in' }} onClick={() => setLightboxSrc(images.plate)} />
                  <div className={styles.detailImageFooter}>
                    <span className={styles.detailImageLabel}>Plaque</span>
                    <button className={styles.downloadBtn} onClick={() => downloadImage(images.plate, `${plate}_plaque_${dateSlug}.jpg`)}>↓</button>
                  </div>
                </div>
              )}
              {images.vehicle && (
                <div className={styles.detailImage}>
                  <img className={styles.detailImg} src={images.vehicle} alt="Véhicule" style={{ cursor: 'zoom-in' }} onClick={() => setLightboxSrc(images.vehicle)} />
                  <div className={styles.detailImageFooter}>
                    <span className={styles.detailImageLabel}>Véhicule</span>
                    <button className={styles.downloadBtn} onClick={() => downloadImage(images.vehicle, `${plate}_vehicule_${dateSlug}.jpg`)}>↓</button>
                  </div>
                </div>
              )}
              {images.detection && (
                <div className={styles.detailImage}>
                  <img className={styles.detailImg} src={images.detection} alt="Contexte" style={{ cursor: 'zoom-in' }} onClick={() => setLightboxSrc(images.detection)} />
                  <div className={styles.detailImageFooter}>
                    <span className={styles.detailImageLabel}>Contexte</span>
                    <button className={styles.downloadBtn} onClick={() => downloadImage(images.detection, `${plate}_contexte_${dateSlug}.jpg`)}>↓</button>
                  </div>
                </div>
              )}
            </>
          )}

          {detection.known_plate && (
            <div className={styles.knownPlateDetail}>
              <span className={styles.knownPlateDetailLabel}>Plaque connue</span>
              <span className={styles.knownPlateDetailName}>
                {detection.known_plate.name_displayed ?? detection.known_plate.name}
              </span>
              {detection.known_plate.name_displayed && detection.known_plate.name_displayed !== detection.known_plate.name && (
                <span className={styles.knownPlateDetailSub}>{detection.known_plate.name}</span>
              )}
              {detection.known_plate.user && (
                <span className={styles.knownPlateDetailSub}>
                  {detection.known_plate.user.first_name} {detection.known_plate.user.last_name}
                </span>
              )}
            </div>
          )}

          <div className={styles.detailMeta}>
            {detection.uncertain_chars?.length > 0 && (
              <div className={styles.detailMetaItem}>
                <span className={styles.detailMetaLabel}>Caractère incertain</span>
                <span className={styles.detailMetaValue}>
                  {detection.uncertain_chars.map(c => `${c.char}(${c.confidence}%)`).join(', ')}
                </span>
              </div>
            )}
            <div className={styles.detailMetaItem}>
              <span className={styles.detailMetaLabel}>Détecté le</span>
              <span className={styles.detailMetaValue}>
                {dayjs(detection.detected_at).format('DD/MM/YYYY à HH:mm:ss')}
              </span>
            </div>
          </div>

          {detection.camera?.id && isOtherSystem && recordStatus === null && (
            <div className={styles.detailRecordAction}>
              <span className={styles.recordStatusText}>
                Passez sur le site « {systemLabel(detection.system)} » pour lancer un enregistrement.
              </span>
              <button className={styles.recordRetryBtn} onClick={() => onSwitchSystem(detection.system.id)}>
                Changer de site
              </button>
            </div>
          )}

          {detection.camera?.id && !(isOtherSystem && recordStatus === null) && (
            <div className={styles.detailRecordAction}>
              {recordStatus === null && (
                <button className={styles.recordBtn} onClick={handleRequestRecord}>
                  ▶ Enregistrement ±15s
                </button>
              )}
              {recordStatus === 'pending' && (
                <span className={styles.recordStatusText}>Enregistrement en cours de création…</span>
              )}
              {recordStatus === 'success' && (
                <>
                  <span className={styles.recordSuccess}>Enregistrement demandé</span>
                  {recordId && (
                    <a className={styles.recordLink} href={`/records/${recordId}`}>
                      Voir l'enregistrement →
                    </a>
                  )}
                  <button className={styles.recordRetryBtn} onClick={() => { setRecordStatus(null); setRecordId(null); }}>
                    Nouvelle demande
                  </button>
                </>
              )}
              {recordStatus === 'error' && (
                <>
                  <span className={styles.recordError}>Erreur lors de la demande</span>
                  <button className={styles.recordRetryBtn} onClick={() => setRecordStatus(null)}>
                    Réessayer
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Pagination({ current, total, loading, onChange }) {
  if (total <= 1) return null;

  const pages = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else if (current <= 4) {
    pages.push(1, 2, 3, 4, 5, '…', total);
  } else if (current >= total - 3) {
    pages.push(1, '…', total - 4, total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, '…', current - 1, current, current + 1, '…', total);
  }

  return (
    <div className={styles.pagination}>
      <button
        className={styles.pageBtn}
        onClick={() => onChange(current - 1)}
        disabled={current === 1 || loading}
      >
        ‹
      </button>
      {pages.map((p, i) =>
        typeof p === 'number' ? (
          <button
            key={i}
            className={p === current ? styles.pageBtnActive : styles.pageBtn}
            onClick={() => onChange(p)}
            disabled={p === current || loading}
          >
            {p}
          </button>
        ) : (
          <span key={i} className={styles.pageSep}>{p}</span>
        )
      )}
      <button
        className={styles.pageBtn}
        onClick={() => onChange(current + 1)}
        disabled={current === total || loading}
      >
        ›
      </button>
    </div>
  );
}

function SystemsPicker({ systems, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const allSelected = systems.every(s => selectedIds.includes(s.id));

  let label;
  if (allSelected) label = 'Tous les sites';
  else if (selectedIds.length === 1) {
    const selected = systems.find(s => s.id === selectedIds[0]);
    label = selected ? systemLabel(selected) : 'Site actuel';
  }
  else label = `${selectedIds.length} sites`;

  // At least one site stays selected
  const toggle = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter(x => x !== id)
      : [...selectedIds, id];
    if (next.length) onChange(next);
  };

  return (
    <div className={styles.systemsPicker} ref={containerRef}>
      <button
        type="button"
        className={styles.selectInput}
        onClick={() => setOpen(v => !v)}
        style={{ minWidth: 160, textAlign: 'left' }}
      >
        {label}
      </button>
      {open && (
        <div className={styles.systemsMenu}>
          <label className={styles.systemsOption}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={allSelected}
              onChange={() => onChange(allSelected ? [] : systems.map(s => s.id))}
            />
            <span>Tous les sites</span>
          </label>
          <div className={styles.systemsSeparator} />
          {systems.map(s => (
            <label key={s.id} className={styles.systemsOption}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={selectedIds.includes(s.id)}
                onChange={() => toggle(s.id)}
              />
              <span>{systemLabel(s)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// null = current site only: systems_id is omitted and the API uses the x-system-token
function appendSystemsParam(params, systemsIds, systems) {
  if (!systemsIds) return;
  if (systems.length && systems.every(s => systemsIds.includes(s.id))) {
    params.append('systems_id', 'all');
    return;
  }
  systemsIds.forEach(id => params.append('systems_id', id));
}

function localTimeToUTC(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  const utcMinutes = (((h * 60 + m) + new Date().getTimezoneOffset()) % 1440 + 1440) % 1440;
  return `${String(Math.floor(utcMinutes / 60)).padStart(2, '0')}:${String(utcMinutes % 60).padStart(2, '0')}`;
}

const INITIAL_FILTERS = {
  systemsIds: null,
  plate: '',
  plateMode: 'contains',
  cameraId: '',
  dateFrom: '',
  dateTo: '',
  timeFrom: '',
  timeTo: '',
  knownOnly: '',
  vehicleColor: '',
  direction: '',
  sortBy: 'detected_at',
  sortOrder: 'desc',
};

const Anpr = () => {
  const { darkMode } = useTheme();
  const isDesktop = useDesktop();
  const { system } = useAppContext();
  const { setSystem } = useAuth();

  // Only sites recording ANPR history (lapi_history on the site and on a camera)
  const [anprSystems, setAnprSystems] = useState([]);
  const hasMultipleSystems = anprSystems.length > 1;

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [lapiCameras, setLapiCameras] = useState([]);
  const [facets, setFacets] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [expandedId, setExpandedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedDetections, setSelectedDetections] = useState(new Map());
  const [zipProgress, setZipProgress] = useState(null); // null | { done, total }

  // Facets and cameras follow the selected sites
  const systemsKey = filters.systemsIds ? filters.systemsIds.join(',') : '';
  useEffect(() => {
    const params = new URLSearchParams();
    appendSystemsParam(params, filters.systemsIds, anprSystems);
    const query = params.toString() ? `?${params}` : '';

    api.get(`/anpr-detections/facets${query}`)
      .then(res => setFacets(res.data))
      .catch(() => {});
    api.get(`/anpr-detections/cameras${query}`)
      .then(res => {
        const cameras = res.data ?? [];
        setLapiCameras(cameras);
        setFilters(prev => (
          prev.cameraId && !cameras.some(c => c.id === prev.cameraId) ? { ...prev, cameraId: '' } : prev
        ));
      })
      .catch(() => {});
  }, [systemsKey]);

  useEffect(() => {
    api.get('/anpr-detections/systems')
      .then(res => setAnprSystems(res.data ?? []))
      .catch(() => {});
    search(1, INITIAL_FILTERS);
  }, []);

  const selectedSystemsIds = filters.systemsIds ?? (system?.id ? [system.id] : []);
  const isMultiSystemSearch = selectedSystemsIds.length > 1;

  const handleSystemsChange = (ids) => {
    // Unchecking "all" (empty list) goes back to the current site
    const onlyCurrent = !ids.length || (ids.length === 1 && ids[0] === system?.id);
    setFilter('systemsIds', onlyCurrent ? null : ids);
  };

  const handleSwitchSystem = async (systemID) => {
    try {
      const response = await api.get(`/systems/${systemID}/select`);
      await setSystem({ systemToken: response.data.systemToken });
      window.location.reload();
    } catch (err) {
      console.error('[ANPR] switch system error:', err);
    }
  };

  const search = useCallback(async (pageNum = 1, currentFilters = filters, clearSelection = false) => {
    setLoading(true);
    setHasSearched(true);
    setExpandedId(null);
    if (clearSelection) {
      setSelectedIds(new Set());
      setSelectedDetections(new Map());
    }

    try {
      const params = new URLSearchParams({
        page: pageNum,
        limit: 20,
        sort_by: currentFilters.sortBy,
        sort_order: currentFilters.sortOrder,
      });

      appendSystemsParam(params, currentFilters.systemsIds, anprSystems);
      if (currentFilters.plate)        params.append('plate', currentFilters.plate);
      if (currentFilters.plateMode !== 'contains') params.append('plate_mode', currentFilters.plateMode);
      if (currentFilters.cameraId)     params.append('cameras_id', currentFilters.cameraId);
      if (currentFilters.dateFrom)     params.append('date_from', new Date(currentFilters.dateFrom + 'T00:00:00').toISOString());
      if (currentFilters.dateTo)       params.append('date_to',   new Date(currentFilters.dateTo   + 'T23:59:59').toISOString());
      if (currentFilters.timeFrom)     params.append('time_from', localTimeToUTC(currentFilters.timeFrom));
      if (currentFilters.timeTo)       params.append('time_to',   localTimeToUTC(currentFilters.timeTo));
      if (currentFilters.knownOnly)    params.append('known_only', currentFilters.knownOnly);
      if (currentFilters.vehicleColor) params.append('vehicle_color', currentFilters.vehicleColor);
      if (currentFilters.direction)    params.append('direction', currentFilters.direction);

      const response = await api.get(`/anpr-detections/search?${params}`);
      setResults(response.data.results);
      setTotal(response.data.total);
      setPages(response.data.pages);
      setCurrentPage(pageNum);
    } catch (err) {
      console.error('[ANPR] search error:', err);
      setResults([]);
      setTotal(0);
      setPages(0);
    } finally {
      setLoading(false);
    }
  }, [filters, anprSystems]);

  const handleSubmit = (e) => {
    e.preventDefault();
    search(1, filters, true);
  };

  const handleReset = () => {
    setFilters(INITIAL_FILTERS);
    setResults([]);
    setHasSearched(false);
    setTotal(0);
    setPages(0);
    setCurrentPage(1);
    setSelectedIds(new Set());
    setSelectedDetections(new Map());
  };

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const toggleCard = (id) => setExpandedId(prev => (prev === id ? null : id));

  const toggleSelect = (id) => {
    const detection = results.find(r => r.id === id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setSelectedDetections(prev => {
      const next = new Map(prev);
      next.has(id) ? next.delete(id) : next.set(id, detection);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allCurrentSelected = results.every(r => selectedIds.has(r.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      allCurrentSelected
        ? results.forEach(r => next.delete(r.id))
        : results.forEach(r => next.add(r.id));
      return next;
    });
    setSelectedDetections(prev => {
      const next = new Map(prev);
      allCurrentSelected
        ? results.forEach(r => next.delete(r.id))
        : results.forEach(r => next.set(r.id, r));
      return next;
    });
  };

  const handleDownloadZip = async () => {
    const selected = Array.from(selectedDetections.values());
    setZipProgress({ done: 0, total: selected.length });
    await downloadSelectionAsZip(selected, (done, total) => setZipProgress({ done, total }));
    setZipProgress(null);
  };

  return (
    <>
      {isDesktop && (
        <div className={BaseStyle.header}>
          <div className={BaseStyle.left}>
            <div className={BaseStyle.iconContainer}>
              <img
                src={`https://cdn.codnum.fr/watch4safe/assets/images/camera_${darkMode ? 'black' : 'white'}.svg`}
                alt=""
                height="20"
                width="20"
              />
            </div>
            <div className={BaseStyle.description}>
              <span className={BaseStyle.title}>Détections ANPR</span>
              <span className={BaseStyle.subTitle}>Recherche dans les passages de véhicules</span>
            </div>
          </div>
        </div>
      )}

      {/* Search panel */}
      <form className={styles.searchPanel} onSubmit={handleSubmit}>
        {/* Main search row */}
        <div className={styles.searchRow}>
          {hasMultipleSystems && (
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Sites</span>
              <SystemsPicker
                systems={anprSystems}
                selectedIds={selectedSystemsIds}
                onChange={handleSystemsChange}
              />
            </div>
          )}

          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Plaque</span>
            <input
              className={styles.input}
              type="text"
              placeholder="AB-123-CD"
              value={filters.plate}
              onChange={e => setFilter('plate', e.target.value.toUpperCase())}
              style={{ minWidth: 160, letterSpacing: 1 }}
            />
          </div>

          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Mode</span>
            <div className={styles.modeGroup}>
              {[
                { value: 'contains', label: 'Contient' },
                { value: 'exact', label: 'Exact' },
                { value: 'fuzzy', label: 'Tolérant' },
              ].map(m => (
                <button
                  key={m.value}
                  type="button"
                  className={filters.plateMode === m.value ? styles.modeBtnActive : styles.modeBtn}
                  onClick={() => setFilter('plateMode', m.value)}
                  title={
                    m.value === 'fuzzy'
                      ? 'Recherche tolérante aux confusions visuelles (0↔O, 8↔B…)'
                      : undefined
                  }
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {lapiCameras.length > 0 && (
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Caméra</span>
              <select
                className={styles.selectInput}
                value={filters.cameraId}
                onChange={e => setFilter('cameraId', e.target.value)}
                style={{ minWidth: 160 }}
              >
                <option value="">Toutes</option>
                {isMultiSystemSearch
                  ? selectedSystemsIds.map(id => {
                      const siteCameras = lapiCameras.filter(c => c.system?.id === id);
                      if (!siteCameras.length) return null;
                      return (
                        <optgroup key={id} label={systemLabel(siteCameras[0].system)}>
                          {siteCameras.map(c => (
                            <option key={c.id} value={c.id}>{c.name_displayed ?? c.name}</option>
                          ))}
                        </optgroup>
                      );
                    })
                  : lapiCameras.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name_displayed ?? c.name}
                      </option>
                    ))}
              </select>
            </div>
          )}

          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Trier par</span>
            <select
              className={styles.selectInput}
              value={filters.sortBy}
              onChange={e => setFilter('sortBy', e.target.value)}
              style={{ minWidth: 130 }}
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Ordre</span>
            <select
              className={styles.selectInput}
              value={filters.sortOrder}
              onChange={e => setFilter('sortOrder', e.target.value)}
              style={{ minWidth: 100 }}
            >
              <option value="desc">Récent d'abord</option>
              <option value="asc">Ancien d'abord</option>
            </select>
          </div>

          <button
            type="button"
            className={styles.advancedToggle}
            onClick={() => setShowAdvanced(v => !v)}
            style={{ alignSelf: 'flex-end', marginBottom: 8 }}
          >
            <span className={`${styles.arrow} ${showAdvanced ? styles.arrowOpen : ''}`}>›</span>
            Filtres avancés
          </button>

          <div style={{ flex: 1 }} />

          <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-end' }}>
            <button type="button" className={styles.resetBtn} onClick={handleReset}>
              Réinitialiser
            </button>
            <button type="submit" className={styles.searchBtn} disabled={loading}>
              {loading ? 'Recherche…' : 'Rechercher'}
            </button>
          </div>
        </div>

        {/* Advanced filters */}
        {showAdvanced && (
          <div className={styles.advancedPanel}>
            <div className={styles.searchRow}>
              <div className={styles.filterGroup}>
                <span className={styles.filterLabel}>Date début</span>
                <input
                  className={styles.input}
                  type="date"
                  value={filters.dateFrom}
                  onChange={e => setFilter('dateFrom', e.target.value)}
                />
              </div>
              <div className={styles.filterGroup}>
                <span className={styles.filterLabel}>Date fin</span>
                <input
                  className={styles.input}
                  type="date"
                  value={filters.dateTo}
                  onChange={e => setFilter('dateTo', e.target.value)}
                />
              </div>
              <div className={styles.filterGroup}>
                <span className={styles.filterLabel}>Heure début</span>
                <input
                  className={styles.input}
                  type="time"
                  value={filters.timeFrom}
                  onChange={e => setFilter('timeFrom', e.target.value)}
                  style={{ minWidth: 110 }}
                />
              </div>
              <div className={styles.filterGroup}>
                <span className={styles.filterLabel}>Heure fin</span>
                <input
                  className={styles.input}
                  type="time"
                  value={filters.timeTo}
                  onChange={e => setFilter('timeTo', e.target.value)}
                  style={{ minWidth: 110 }}
                />
              </div>
            </div>
            <div className={styles.searchRow}>
              {facets?.vehicle_color?.length > 0 && (
                <div className={styles.filterGroup}>
                  <span className={styles.filterLabel}>Couleur véhicule</span>
                  <select
                    className={styles.selectInput}
                    value={filters.vehicleColor}
                    onChange={e => setFilter('vehicleColor', e.target.value)}
                  >
                    <option value="">Toutes</option>
                    {facets.vehicle_color.map(f => (
                      <option key={f.value} value={f.value}>
                        {translateColor(f.value)} ({f.count})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {facets?.direction?.length > 0 && (
                <div className={styles.filterGroup}>
                  <span className={styles.filterLabel}>Direction</span>
                  <select
                    className={styles.selectInput}
                    value={filters.direction}
                    onChange={e => setFilter('direction', e.target.value)}
                  >
                    <option value="">Toutes</option>
                    {facets.direction.map(f => (
                      <option key={f.value} value={f.value}>
                        {translateDirection(f.value)} ({f.count})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className={styles.filterGroup}>
                <span className={styles.filterLabel}>Reconnaissance</span>
                <select
                  className={styles.selectInput}
                  value={filters.knownOnly}
                  onChange={e => setFilter('knownOnly', e.target.value)}
                >
                  <option value="">Toutes</option>
                  <option value="true">Connues</option>
                  <option value="false">Inconnues</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Results */}
      <div className={styles.resultsArea}>
        {loading && (
          <div className={styles.loadingRow}>Recherche en cours…</div>
        )}

        {!loading && hasSearched && (
          <div className={styles.resultsHeader}>
            <span className={styles.resultCount}>
              {total === 0
                ? 'Aucune détection trouvée'
                : `${total} détection${total > 1 ? 's' : ''} trouvée${total > 1 ? 's' : ''}`}
            </span>
            {results.length > 0 && (
              <div className={styles.selectionBar}>
                <button className={styles.selectAllBtn} onClick={toggleSelectAll}>
                  {results.every(r => selectedIds.has(r.id)) ? 'Désélectionner la page' : 'Sélectionner la page'}
                </button>
                {selectedIds.size > 0 && (
                  <>
                    <span className={styles.selectionCount}>{selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}</span>
                    <button
                      className={styles.zipBtn}
                      onClick={handleDownloadZip}
                      disabled={zipProgress !== null}
                    >
                      {zipProgress
                        ? `${zipProgress.done}/${zipProgress.total}…`
                        : '↓ Télécharger le ZIP'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {!loading && !hasSearched && (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>🔍</span>
            <span className={styles.emptyTitle}>Recherchez des détections ANPR</span>
            <span className={styles.emptySubtitle}>
              Utilisez les filtres ci-dessus pour retrouver des passages de véhicules
            </span>
          </div>
        )}

        {!loading && hasSearched && results.length === 0 && (
          <div className={styles.emptyState}>
            <span className={styles.emptyTitle}>Aucune détection</span>
            <span className={styles.emptySubtitle}>Essayez d'élargir vos critères de recherche</span>
          </div>
        )}

        {!loading && results.map(detection => (
          <DetectionCard
            key={detection.id}
            detection={detection}
            expanded={expandedId === detection.id}
            onClick={() => toggleCard(detection.id)}
            selected={selectedIds.has(detection.id)}
            onSelect={toggleSelect}
            showSystem={isMultiSystemSearch}
            isOtherSystem={!!detection.system && !!system?.id && detection.system.id !== system.id}
            onSwitchSystem={handleSwitchSystem}
          />
        ))}

        {!loading && (
          <Pagination
            current={currentPage}
            total={pages}
            loading={loading}
            onChange={page => search(page)}
          />
        )}
      </div>
    </>
  );
};

export default Anpr;
